import { createEffect, createSignal, For, Show } from 'solid-js';
import { useLocation } from '@solidjs/router';
import { useApi, isOk } from '../api';
import { firstError } from '../api/envelope';
import type { BotGroupDto } from '../api/generated/schemas';
import { useActiveBot } from '../state/activeBot';
import { useTranslation } from '../hooks/useTranslation';
import { useToast } from './MessageBox';
import { showConfirm } from './Dialog';
import Modal from './Modal';
import Text from './Text';
import BotAvatarStack from './BotAvatarStack';
import BotStatusBadge from './BotStatusBadge';
import type { BotRegistryEntry } from './BotSelector';
import styles from './BotGroupsSection.module.css';

interface GroupDraft {
    id: string | null;
    name: string;
    botIds: string[];
}

interface BotGroupsSectionProps {
    bots: readonly BotRegistryEntry[];
}

export default function BotGroupsSection(props: BotGroupsSectionProps) {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const { groups, botsInGroup, refresh } = useActiveBot();

    const location = useLocation();
    const [draft, setDraft] = createSignal<GroupDraft | null>(null);
    const [saving, setSaving] = createSignal(false);
    let sectionRef: HTMLElement | undefined;

    createEffect(() => {
        if (location.hash === '#groups') queueMicrotask(() => sectionRef?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    });

    const botsByName = () => [...props.bots].sort((left, right) => left.minecraftUsername.localeCompare(right.minecraftUsername));

    const openNew = () => setDraft({ id: null, name: '', botIds: [] });
    const openEdit = (group: BotGroupDto) => setDraft({ id: group.id, name: group.name, botIds: [...group.botIds] });

    const toggleMember = (botId: string) => setDraft(prev => prev && ({
        ...prev,
        botIds: prev.botIds.includes(botId) ? prev.botIds.filter(id => id !== botId) : [...prev.botIds, botId],
    }));

    const canSave = () => {
        const current = draft();
        return !!current && current.name.trim().length > 0 && current.botIds.length > 0 && !saving();
    };

    const save = async () => {
        const current = draft();
        if (!current || !canSave()) return;
        setSaving(true);
        const body = { name: current.name.trim(), botIds: current.botIds };
        const res = current.id
            ? await api.botGroup.updateBotGroup(current.id, body)
            : await api.botGroup.createBotGroup(body);
        setSaving(false);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('BOT_GROUPS_SAVE_FAILED'));
            return;
        }
        toast?.info(t('BOT_GROUPS_SAVED'));
        setDraft(null);
        await refresh();
    };

    const remove = async (group: BotGroupDto) => {
        if (!await showConfirm(t('BOT_GROUPS_DELETE_CONFIRM', group.name), true)) return;
        const res = await api.botGroup.deleteBotGroup(group.id);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('BOT_GROUPS_DELETE_FAILED'));
            return;
        }
        toast?.info(t('BOT_GROUPS_DELETED'));
        await refresh();
    };

    return (
        <section ref={sectionRef} id="groups" class="v" style={{ gap: 'var(--spacing-md)' }}>
            <div class={styles.header}>
                <h2 class={styles.title}>
                    <i class="fa-solid fa-layer-group" /> <Text val="BOT_GROUPS_TITLE" />
                    <span class={styles.count}>({groups().length})</span>
                </h2>
                <button type="button" class="btn btn-secondary btn-sm" onClick={openNew}>
                    <i class="fa-solid fa-plus" /> <Text val="BOT_GROUPS_NEW" />
                </button>
            </div>

            <Show when={groups().length > 0} fallback={
                <div class={`fcard v ${styles.empty}`}>
                    <i class="fa-solid fa-layer-group" />
                    <p><Text val="BOT_GROUPS_EMPTY" /></p>
                </div>
            }>
                <div class="v" style={{ gap: 'var(--spacing-sm)' }}>
                    <For each={groups()}>{(group) => (
                        <div class={`fcard ${styles.groupRow}`}>
                            <BotAvatarStack bots={botsInGroup(group.botIds)} max={5} />
                            <div class={styles.groupInfo}>
                                <span class={styles.groupName}>{group.name}</span>
                                <span class={styles.groupMeta}>{t('BOT_GROUPS_MEMBER_COUNT', String(botsInGroup(group.botIds).length))}</span>
                            </div>
                            <button type="button" class="btn btn-secondary btn-sm" onClick={() => openEdit(group)}>
                                <i class="fa-solid fa-pen-to-square" /> <Text val="BOT_GROUPS_EDIT" />
                            </button>
                            <button type="button" class="btn btn-danger btn-sm btn-icon" title={t('BOT_GROUPS_DELETE')} onClick={() => void remove(group)}>
                                <i class="fa-solid fa-trash" />
                            </button>
                        </div>
                    )}</For>
                </div>
            </Show>

            <Modal
                open={draft() !== null}
                onClose={() => setDraft(null)}
                title={t(draft()?.id ? 'BOT_GROUPS_EDIT_TITLE' : 'BOT_GROUPS_NEW_TITLE')}
                maxWidth="32rem"
                busy={saving()}
            >
                <Show when={draft()}>
                    {(current) => (
                        <form class="v" style={{ gap: 'var(--spacing-md)' }} onSubmit={(e) => { e.preventDefault(); void save(); }}>
                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="BOT_GROUPS_NAME" /></span>
                                <input
                                    type="text"
                                    value={current().name}
                                    maxLength={64}
                                    onInput={(e) => { const name = e.currentTarget.value; setDraft(prev => prev && ({ ...prev, name })); }}
                                />
                            </label>
                            <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="BOT_GROUPS_MEMBERS" /></span>
                                <div class={styles.memberList}>
                                    <For each={botsByName()}>{(bot) => (
                                        <label class={styles.memberRow}>
                                            <input
                                                type="checkbox"
                                                checked={current().botIds.includes(bot.id)}
                                                onChange={() => toggleMember(bot.id)}
                                            />
                                            <img class={styles.avatar} src={`https://mc-heads.net/avatar/${bot.minecraftUuid}/24`} alt="" />
                                            <span class={styles.memberName}>{bot.minecraftUsername}</span>
                                            <BotStatusBadge state={bot.state} compact />
                                        </label>
                                    )}</For>
                                </div>
                                <Show when={current().botIds.length === 0}>
                                    <span class={styles.fieldHint}><Text val="BOT_GROUPS_MEMBERS_REQUIRED" /></span>
                                </Show>
                            </div>
                            <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)' }}>
                                <button type="button" class="btn btn-secondary" onClick={() => setDraft(null)}>
                                    <Text val="CANCEL" />
                                </button>
                                <button type="submit" class="btn btn-primary" disabled={!canSave()}>
                                    <Show when={saving()} fallback={<i class="fa-solid fa-floppy-disk" />}>
                                        <i class="fa-solid fa-spinner fa-spin" />
                                    </Show>
                                    {' '}<Text val="SAVE" />
                                </button>
                            </div>
                        </form>
                    )}
                </Show>
            </Modal>
        </section>
    );
}
